import React, { useState, useEffect, useContext } from 'react';
import { SelectPicker } from 'rsuite';
import { motion, AnimatePresence } from "framer-motion";
import MultiLingualContent from "../config/multilingualContent";
import { getPublishedSankeys, getSankeyDetails, getCountries } from "../functions";
import { LanguageContext } from "../config/multilingualContext";

import SankeyD3Chart from './SankeyD3Chart';
import SectionTitle from "./SectionTitle";
import YearSlider from './YearSlider';

import '../assets/css/flux.css';
import 'rsuite/dist/rsuite.min.css';

const FluxContent = () => {
    const { language, toggleLanguage } = useContext(LanguageContext);
    const [publishedCharts, setPublishedCharts] = useState([]);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [years, setYears] = useState([]);
    const [currentYear, setCurrentYear] = useState(null);
    const [dataCache, setDataCache] = useState({});
    const [playing, setPlaying] = useState(false);


    useEffect(() => {
        async function fetchSankeys() {
            try {
                let countriesData = await getCountries();
                countriesData = countriesData?.map((item) => ({
                    label: {
                        french: item.label_fr ?? "",
                        english: item.label_en ?? "",
                        portuguais: item.label_pr ?? "",
                        swahili: item.label_sw ?? "",
                    }[language],
                    value: item.id
                }));
                setCountries(countriesData);
                setSelectedCountry(countriesData.length ? countriesData[0].value : 26);

                const data = await getPublishedSankeys();
                setPublishedCharts(data);
                setSelectedId(data.length ? data[0].id : null);
            } catch (error) {
                console.log(error);
            }        }
        fetchSankeys();
    }, []);

    useEffect(() => {
        async function fetchYearData() {
            if (!selectedId && !currentYear) return;
            try {
                setLoading(true);
                let data = null;
                if (dataCache[selectedId] && dataCache[selectedId][currentYear] && dataCache[selectedId][currentYear][selectedCountry]) {
                    data = dataCache[selectedId][currentYear][selectedCountry];
                    setChartData(data);
                } else{
                    data = await getSankeyDetails(selectedId, currentYear, selectedCountry);
                    setChartData(data);
                    setDataCache(prev => ({ ...prev, [selectedId]: { [currentYear]: {selectedCountry: data } } }));
                }
                let allYears = data?.years? data.years: [];
                allYears = allYears.map(Number);
                 setYears(prev => {
                    if (prev.length === allYears.length && 
                        prev.every((y, i) => y === allYears[i])) {
                        return prev;
                    }
                    return allYears;
                });
                
                // 🔑 ONLY set initial year on FIRST LOAD for this sankey/country
                // Do NOT reset when user manually changes year via slider
                if (currentYear === null || !allYears.includes(currentYear)) {
                    if (allYears.length > 0) {
                        setCurrentYear(allYears[0]);
                    }
                }

            } catch (error) {
                console.log(error);
            }
            setLoading(false);   
        }
        fetchYearData();
     }, [currentYear, selectedId, selectedCountry]);


    return (
        <div className="container about-list-items">
            <SectionTitle pageTitle="sankey" />
            <div className="row mt-2">
                <div className="col-lg-4 col-xl-4 col-md-4  col-xs-12 mt-2">
                    <SelectPicker
                        label={<MultiLingualContent contentID="fluxTitle" />}
                        value={selectedId}
                        onChange={setSelectedId}
                        data={publishedCharts?.map(c => ({ label: c.name, value: c.id })) ?? []}
                        style={{width: 500}}
                        
                    />
                </div>
                <div className="col-lg-4 col-xl-4 col-md-4  col-xs-12 mt-2">
                    <SelectPicker
                        label={<MultiLingualContent contentID="country" />}
                        value={selectedCountry}
                        onChange={setSelectedCountry}
                        data={countries ?? []}
                        cleanable
                        style={{width: 500}}
                        block
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-lg-12 col-xl-12 col-md-12 col-xs-12">
                    {chartData && chartData?.data.length != 0 ? (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key="chart-container"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                            >
                                <SankeyD3Chart json={chartData} language={language} />
                            </motion.div>
                        </AnimatePresence>
                    ) : (
                        <div className='row no-data-message'><MultiLingualContent contentID="nodata" /></div>
                    )}
                </div>
            </div>
            <div className="mb-3">
            {years.length > 0 && (
                <YearSlider
                    years={years}
                    currentYear={currentYear}
                    setCurrentYear={setCurrentYear}
                    playing={playing}
                    setPlaying={setPlaying}
                />
            )}
            </div>
        </div>


    );
};

export default FluxContent;